import * as React from "react";
import {
  ThemeProvider,
  responsiveFontSizes,
  createTheme,
} from "@mui/material/styles";
import CookieManager from "@/utils/CookieManager";

export const ThemeModeContext = React.createContext({
  mode: "light",
  setMode: () => {},
} as { mode: string; setMode: (mode: string) => void });

const theme = {
  dark: responsiveFontSizes(
    createTheme({
      typography: {
        fontFamily: '"Open Sans", Helvetica, Arial, Verdana, sans-serif',
        fontSize: 16,
        htmlFontSize: 16,
        body1: { fontSize: "1rem", lineHeight: 1.5 },
        body2: { fontSize: "1rem", lineHeight: 1.5 },
        caption: { fontSize: "1rem", lineHeight: 1.5 },
        overline: { fontSize: "1rem", lineHeight: 1.5 },
      },
      palette: {
        mode: "dark",
        primary: { main: "#c8102e" },
        secondary: { main: "#4b4f55" },
      },
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              variants: [
                {
                  props: { variant: "contained", color: "secondary" },
                  style: ({ theme }: any) => ({
                    backgroundColor: theme.palette.secondary.main,
                    color: "#fff",
                    borderColor: theme.palette.secondary.main,
                    "&:hover": {
                      backgroundColor: "#3f4246",
                    },
                  }),
                },
                {
                  props: { variant: "outlined", color: "secondary" },
                  style: ({ theme }: any) => ({
                    backgroundColor: theme.palette.secondary.main,
                    color: "#fff",
                    borderColor: theme.palette.secondary.main,
                    "&:hover": {
                      backgroundColor: "#3f4246",
                    },
                  }),
                },
                {
                  props: { variant: "text", color: "secondary" },
                  style: ({ theme }: any) => ({
                    backgroundColor: theme.palette.secondary.main,
                    color: "#fff",
                    "&:hover": {
                      backgroundColor: "#3f4246",
                    },
                  }),
                },
              ],
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            head: ({ theme }: any) => ({
              backgroundColor: theme.palette.secondary.main,
              color: "#000",
              "& .MuiTypography-root": {
                color: "#000",
              },
            }),
          },
        },
        MuiToolbar: {
          styleOverrides: {
            root: { backgroundColor: "#000000", color: "#fff" },
          },
        },
        MuiTypography: {
          styleOverrides: {
            root: {
              "&.MuiTypography-root": { color: "#fff" },
              maxWidth: "min(60ch, 100%)",
            },
          },
        },
        MuiSvgIcon: {
          styleOverrides: { root: { "&:hover": { color: "#c8102e" } } },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
              color: "#fff",
              "&.MuiInputLabel-shrink": {
                fontSize: "1rem",
                transform: "translate(14px, -12px) scale(1)",
              },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: { root: { fontSize: "1rem", color: "#fff" } },
        },
      },
    }),
  ),
  light: responsiveFontSizes(
    createTheme({
      typography: {
        fontFamily: '"Open Sans", Helvetica, Arial, Verdana, sans-serif',
        fontSize: 16,
        htmlFontSize: 16,
        body1: { fontSize: "1rem", lineHeight: 1.5 },
        body2: { fontSize: "1rem", lineHeight: 1.5 },
        caption: { fontSize: "1rem", lineHeight: 1.5 },
        overline: { fontSize: "1rem", lineHeight: 1.5 },
      },
      palette: {
        mode: "light",
        primary: { main: "#c8102e" },
        secondary: { main: "#4b4f55" },
        contrastThreshold: 3,
        tonalOffset: 0.2,
      },
      components: {
        MuiButton: { styleOverrides: { root: { textTransform: "none" } } },
        MuiTableCell: {
          styleOverrides: {
            head: ({ theme }: any) => ({
              backgroundColor: theme.palette.secondary.main,
              color: "#000",
              "& .MuiTypography-root": {
                color: "#000",
              },
            }),
          },
        },
        MuiSvgIcon: {
          styleOverrides: { root: { "&:hover": { color: "#c8102e" } } },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              fontSize: "1rem",
              color: "#000",
              "&.MuiInputLabel-shrink": {
                fontSize: "1rem",
                transform: "translate(14px, -12px) scale(1)",
              },
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: { root: { fontSize: "1rem", color: "#000" } },
        },
        MuiTypography: {
          styleOverrides: {
            root: {
              maxWidth: "min(60ch, 100%)",
            },
          },
        },
      },
    }),
  ),
};

const getTheme = (mode: string) => (mode === "dark" ? theme.dark : theme.light);

export default function AppThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const initialMode = CookieManager.get("theme-mode") || "light";
  const [mode, setMode] = React.useState(initialMode);
  const theme = getTheme(mode);

  React.useEffect(() => {
    try {
      CookieManager.set("theme-mode", mode, { days: 365 });
    } catch {
      // ignore
    }
  }, [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, setMode }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
